/**
 * Client for `/api/pct/v1/analyses/{id}/shareholders` — the persistent cap table behind the Vote
 * Room. Enum-string fields use Title Case (`DQI`, `Section 318`, `Manual`) per
 * `.cursor/rules/enum-string-title-case.mdc`.
 */
import { API_CONFIG } from '../config';
import { pctBearerHeaders, pctEnsureOk } from './pctApiHttp';

export interface ShareholderRow {
  id: string;
  analysisId: string;
  name: string;
  email: string;
  shares: number;
  excluded: boolean;
  exclusionType: 'DQI' | 'Section 318' | 'Manual' | null;
  linkedDqiClientId: string | null;
  section318Relationship: string | null;
  importedFromFileName: string | null;
  emailPrecheckStatus: 'Pending' | 'Queued' | 'Sent' | 'Failed' | 'Confirmed' | null;
  emailPrecheckSentAt: string | null;
  emailPrecheckMessageId: string | null;
  emailPrecheckError: string | null;
  createdAt: string;
  updatedAt: string;
}

export type ShareholderPrecheckConfirmPageResult = {
  outcome: string;
  title: string;
  message: string;
  isError: boolean;
  companyName?: string | null;
  shareholderName?: string | null;
};

export interface ShareholderUpsertItem {
  id?: string | null;
  name: string;
  email?: string;
  shares: number;
  excluded: boolean;
  exclusionType?: 'DQI' | 'Section 318' | 'Manual' | null;
  linkedDqiClientId?: string | null;
  section318Relationship?: string | null;
  importedFromFileName?: string | null;
}

export async function listShareholders(analysisId: string): Promise<ShareholderRow[]> {
  const headers = await pctBearerHeaders();
  if (!headers) return [];
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.analysisShareholders(analysisId)}`;
  const res = await fetch(url, { method: 'GET', headers });
  await pctEnsureOk(res, 'Failed to load shareholders');
  const json = (await res.json()) as { data?: ShareholderRow[] };
  return json?.data ?? [];
}

export async function bulkUpsertShareholders(
  analysisId: string,
  items: ShareholderUpsertItem[],
  options: { replaceMissing?: boolean } = {}
): Promise<ShareholderRow[]> {
  const headers = await pctBearerHeaders();
  if (!headers) throw new Error('Not signed in.');
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.analysisShareholdersBulkUpsert(analysisId)}`;
  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({ items, replaceMissing: options.replaceMissing ?? true }),
  });
  await pctEnsureOk(res, 'Failed to save shareholders');
  const json = (await res.json()) as { data?: ShareholderRow[] };
  return json?.data ?? [];
}

export async function deleteShareholder(analysisId: string, shareholderId: string): Promise<void> {
  const headers = await pctBearerHeaders();
  if (!headers) throw new Error('Not signed in.');
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.analysisShareholder(analysisId, shareholderId)}`;
  const res = await fetch(url, { method: 'DELETE', headers });
  if (res.status === 204) return;
  await pctEnsureOk(res, 'Failed to remove shareholder');
}

/** Public pre-launch email confirm (no auth) — shareholder clicks link from verification email. */
export async function confirmShareholderPrecheckEmail(params: {
  analysisId: string;
  shareholderId: string;
  token: string;
}): Promise<ShareholderPrecheckConfirmPageResult> {
  const res = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.voteRoomPrecheckConfirmEmail}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      analysisId: params.analysisId,
      shareholderId: params.shareholderId,
      token: params.token,
    }),
  });

  await pctEnsureOk(res, 'Unable to confirm your email address.');
  const json = (await res.json()) as { data?: ShareholderPrecheckConfirmPageResult };
  if (!json?.data) {
    throw new Error('Unable to confirm your email address.');
  }
  return json.data;
}
